import { useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from "react-hook-form";
import { z } from 'zod';

import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';

import SelectFloating from '@/components/atoms/SelectFloating';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";


import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { useCrmConfigsByCrmProjectId } from '@/features/crm_config/hooks/useQuery/useCrmConfigsByCrmProjectId';
import { useUpdateSurvey } from '@/features/survey/hooks/useMutation/useUpdateSurvey';
import { useProjectStore, useSurveyStore } from '@/lib/zustand/useStore';
import { useQueryClient } from '@tanstack/react-query';
import { useGetSurveyById } from '@/features/survey/hooks/useQuery/useGetSurveyById';


export default function EditSurveyDialog({
    showModal,
    setShowModal,
    surveyId
}: {
    showModal: boolean,
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>,
    surveyId: string | null | undefined,
}) {
    const { isLoading, data: surveyData } = useGetSurveyById(surveyId);
    const { isLoading: isLoadingCrms, data: projectCrmsData } = useCrmConfigsByCrmProjectId(surveyData?.project?.crm_project_id);

    const { isPending, mutateAsync: updateSurvey } = useUpdateSurvey();
    const queryClient = useQueryClient();
    const project = useProjectStore(state => state.project);
    const survey = useSurveyStore(state => state.survey);
    const schema = z.object({
        name: z.string({ message: "Name is required" }),
        description: z.string({ message: "Description is required" }),
        crm_config_id: z.string().optional().nullable(),
    })
    type schemaType = z.infer<typeof schema>;
    const { register, handleSubmit, reset, control, formState: { errors } } = useForm<schemaType>({
        resolver: zodResolver(schema)
    })

    useEffect(() => {
        reset({
            name: surveyData?.name,
            description: surveyData?.description || '',
            crm_config_id: String(surveyData?.crm_config_id) || ''
        })
    }, [reset, surveyData])

    const onSubmit = async (data: schemaType) => {
        updateSurvey({
            data: {
                id: surveyData?.id,
                name: data.name,
                description: data.description,
                crm_config_id: data.crm_config_id ? Number(data.crm_config_id) : null

            }
        }).then(() => {
            SuccessPopup("Survey edited successfully")
            setShowModal(false)
        }).catch((err) => {
            console.error(err)
            ErrorPopup(err.message)
        })
        queryClient.invalidateQueries({ queryKey: ['exportData', project?.id, survey?.id] });
    }

    return (
        <>
            {(isLoading || isPending || isLoadingCrms) && (<LoaderWithBackdrop />)}

            <Dialog open={showModal} onOpenChange={setShowModal} defaultOpen={false}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className='ps-3 text-start'>
                            <h2 className="mb-1">Edit Survey</h2>
                            <h3 className="text-sm italic">{surveyData?.name}</h3>
                        </DialogTitle>
                    </DialogHeader>

                    <ScrollArea className="max-h-[60vh] sm:max-h-[70vh] px-3">
                        <div className="flex flex-col gap-2 p-1 mb-3">
                            <div className="m-0 mb-2">
                                <Label className="mb-2">Name</Label>
                                <Input {...register('name')} />
                            </div>

                            <div className="m-0 mb-2">
                                <Label className="mb-2">Description</Label>
                                <Input {...register('description')} />
                            </div>

                            <div className="m-0 mb-2">
                                <SelectFloating
                                    emptyLabel=""
                                    hasEmptyOption={true}
                                    emptyValue={null}

                                    label="Select CRM Config (optional)"
                                    placeholder="Select CRM Config"
                                    name={"crm_config_id"}
                                    errorMessage={errors["crm_config_id"]?.message}
                                    showError={true}
                                    dataArrayWithCustomVal={projectCrmsData?.map(config => [config.name, config.id])}
                                    control={control}

                                    disabled={!projectCrmsData?.length}
                                />
                            </div>

                        </div>

                    </ScrollArea>

                    <DialogFooter>
                        <div className="flex items-stretch justify-end m-0 sm:space-x-2 max-sm:flex-col max-sm:space-y-2">
                            <Button size="sm" variant='secondary' onClick={() => { setShowModal(false) }}>Close</Button>
                            <Button size="sm" variant='default' onClick={() => { handleSubmit(onSubmit)() }}>Submit</Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
